import { useCallback, useEffect, useMemo, useState } from 'react';
import { isStoreViewSelectionInMemberPreferencesEnabled } from '@gonfalon/dogfood-flags/dogfood-flags';
import { getSelectedViewQuery, usePutSelectedView } from '@gonfalon/rest-api/queries/profile';
import { getViewQuery, getViewsQuery } from '@gonfalon/rest-api/queries/views';
import { useQuery } from '@tanstack/react-query';

import { VIEWS_QUERY_STALE_TIME } from './internal/constants';
import type { View } from './types';

interface UseSelectedViewOptions {
  projectKey: string;
  viewKeyQueryParamValue?: string;
  updateResourceFilters?: (selectedViewKey?: string) => void;
}

interface UseSelectedViewReturn {
  selectedView: View | undefined;
  setSelectedView: (view: View | undefined) => void;
  isLoading: boolean;
}

/**
 * Custom hook to manage view selection with user preferences.
 *
 * This hook:
 * - Loads the user's selected view preference for a project
 * - Gives priority to URL param view over user preference
 * - Saves view selections to user preferences with optimistic updates
 * - Returns the effective selected view and functions to manage it
 */
export function useSelectedView({
  projectKey,
  viewKeyQueryParamValue,
  updateResourceFilters,
}: UseSelectedViewOptions): UseSelectedViewReturn {
  const shouldStoreInPreferences = isStoreViewSelectionInMemberPreferencesEnabled();
  const [selectedViewInState, setSelectedViewInState] = useState<View | undefined>(undefined);

  // We get the list of views to speed up display of a selected view
  const {
    data: viewsRes,
    isPending: isLoadingViews,
    isError: isViewsError,
  } = useQuery({
    ...getViewsQuery({
      projectKey,
      header: { 'LD-API-Version': 'beta' },
    }),
    staleTime: VIEWS_QUERY_STALE_TIME,
  });
  const { mutate: updateSelectedView } = usePutSelectedView();

  // If URL param is provided, fetch that specific view (takes precedence)
  const urlParamViewFromList = viewKeyQueryParamValue
    ? viewsRes?.items.find((view) => view.key === viewKeyQueryParamValue)
    : undefined;
  const { data: urlParamViewRes, isLoading: isLoadingUrlParam } = useQuery({
    ...getViewQuery({
      projectKey,
      viewKey: viewKeyQueryParamValue ?? '',
      header: { 'LD-API-Version': 'beta' },
    }),
    enabled: !!viewKeyQueryParamValue && !isLoadingViews && !isViewsError && !urlParamViewFromList,
  });
  const urlParamView = urlParamViewFromList || urlParamViewRes;

  // Get user's selected view key from preferences
  const { data: userPreferenceViewKey, isLoading: isLoadingPreferenceKey } = useQuery({
    ...getSelectedViewQuery({ projKey: projectKey }),
    enabled: shouldStoreInPreferences,
  });

  // If the user has a preference, we fetch the view from the list of views
  const preferenceViewFromList = viewsRes?.items.find((view) => view.key === userPreferenceViewKey?.selectedViewKey);
  const { data: userPreferenceView, isLoading: isLoadingPreferenceView } = useQuery({
    ...getViewQuery({
      projectKey,
      viewKey: userPreferenceViewKey?.selectedViewKey ?? '',
      header: { 'LD-API-Version': 'beta' },
    }),
    enabled:
      shouldStoreInPreferences && !isViewsError && !!userPreferenceViewKey?.selectedViewKey && !preferenceViewFromList,
  });
  const preferenceView = preferenceViewFromList || userPreferenceView;

  const setSelectedView = useCallback(
    (view: View | undefined) => {
      if (!shouldStoreInPreferences) {
        setSelectedViewInState(view);
        return;
      }

      updateSelectedView(
        view
          ? { body: { selectedViewKey: view.key }, projKey: projectKey }
          : { body: { selectedViewKey: '' }, projKey: projectKey },
      );
    },
    [updateSelectedView, projectKey, shouldStoreInPreferences],
  );

  // If the user has a preference stored in memberPrefs, but NO view in the URL param, we update filters to reflect the preference
  // only if the view exists, else clear the selected view.
  // When the views query errors (e.g. 403), preferenceView will be undefined and
  // the stored preference is cleared so stale selections don't persist.
  useEffect(() => {
    if (userPreferenceViewKey?.selectedViewKey && !viewKeyQueryParamValue) {
      if (preferenceView) {
        updateResourceFilters?.(userPreferenceViewKey?.selectedViewKey);
        return;
      }

      // Only clear once the view queries have settled, otherwise a valid
      // preference would be wiped while the view is still loading
      if (!isLoadingViews && !isLoadingPreferenceView) {
        setSelectedView(undefined);
      }
    }
  }, [
    userPreferenceViewKey?.selectedViewKey,
    viewKeyQueryParamValue,
    updateResourceFilters,
    preferenceView,
    setSelectedView,
    isLoadingViews,
    isLoadingPreferenceView,
  ]);

  const selectedView = useMemo(
    () =>
      viewKeyQueryParamValue
        ? urlParamView
        : shouldStoreInPreferences
          ? userPreferenceViewKey?.selectedViewKey
            ? preferenceView
            : undefined
          : selectedViewInState,
    [
      viewKeyQueryParamValue,
      urlParamView,
      shouldStoreInPreferences,
      userPreferenceViewKey?.selectedViewKey,
      preferenceView,
      selectedViewInState,
    ],
  );

  return {
    selectedView: isViewsError ? undefined : selectedView,
    setSelectedView,
    isLoading:
      !isViewsError && (isLoadingViews || isLoadingPreferenceKey || isLoadingPreferenceView || isLoadingUrlParam),
  };
}
